import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'react-bootstrap';
import ServiceSelect from '../../components/selects/serviceSelect';
import FormInput from '../../components/forms/formInput';
import { useMutation, useQueryClient } from 'react-query';
import { createLineItem, updateLineItem } from '../../services/lineItems';
import { onMutateSuccess } from '../../utils/queryResponse';
import { invalidateJobStats } from '../../utils/invalidateStats';
import SbrDrawer from '../../components/sbr/SbrDrawer';

const ListIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
);

const initLineItem = {
    quantity: 0,
    price: 0,
    taxRate: 0,
    taxAmount: 0,
    priceWithTax: 0,
    serviceId: ''
}

const SetLineItemModal = ({
    display,
    toggle,
    lineItemToEdit,
    jobId
}) => {

    const { t } = useTranslation();
    const [lineItem, setLineItem] = useState({ ...initLineItem, jobId })

    const queryClient = useQueryClient()

    const { mutate, isLoading } = useMutation(lineItemToEdit?.id ? updateLineItem : createLineItem, {
        onSuccess: onMutateSuccess(() => {
            invalidateJobStats(queryClient)
            queryClient.invalidateQueries(['jobs', 'details', jobId])
            queryClient.invalidateQueries(['job-activities', jobId])
            onHide()
        })
    })

    const onShow = () => {
        lineItemToEdit?.id ? setLineItem(lineItemToEdit) : setLineItem({ ...initLineItem, jobId })
    }

    const onHide = () => {
        toggle(false)
    }

    const handleChange = (objValue) => {
        const _lineItem = { ...lineItem, ...objValue }

        let _price = _lineItem.pricePerUnit * _lineItem.quantity
        _lineItem.price = _price

        let _taxAmount = (_lineItem.taxRate / 100) * _lineItem.price
        _lineItem.taxAmount = _taxAmount

        let _priceWithTax = _lineItem.price + _lineItem.taxAmount
        _lineItem.priceWithTax = _priceWithTax

        setLineItem(_lineItem)
    }

    const handleServiceSelect = (option) => {
        handleChange({ serviceId: option.value, pricePerUnit: parseFloat(option.ppu), unit: option.unit, quantity: 0 })
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        e.stopPropagation()

        mutate(lineItem)
    }

    return (
        <SbrDrawer
            show={display}
            onHide={onHide}
            onShow={onShow}
            secondary
            backdrop="static"
            title={lineItemToEdit?.id ? t('jobs.editLineItem') : t('jobs.addLineItem')}
            subtitle={t('jobs.addOrUpdateServiceLine')}
            icon={<ListIcon />}
            footer={
                <>
                    <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={onHide} disabled={isLoading}>{t('common.cancel')}</button>
                    <button type='submit' form="set-line-item" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={isLoading}>
                        {isLoading ? t('common.saving') : t('common.save')}
                    </button>
                </>
            }
        >
            <Form id='set-line-item' onSubmit={handleSubmit} style={{ position: 'relative' }}>
                <Row>
                    <Col lg="4">
                        <Form.Group className="mb-2">
                            <Form.Label>
                                {lineItem.serviceId ? t('jobs.pricePerUnit', { price: lineItem.pricePerUnit, unit: lineItem.unit }) : t('jobs.service')}
                                <span className="text-danger">*</span>
                            </Form.Label>
                            <ServiceSelect required value={lineItem.serviceId ?? ''} onChange={(option) => handleServiceSelect(option)} />
                        </Form.Group>
                    </Col>
                    <Col lg="2">
                        <FormInput value={lineItem.quantity} className="mb-2"
                            label={lineItem.serviceId ? lineItem.unit : t('common.quantity')}
                            type="number" min={lineItem.unit === 'Piece' ? 1 : 0.01} step={lineItem.unit === 'Piece' ? 1 : 0.01}
                            onChange={(e) => handleChange({ quantity: e.target.value })}
                            required
                        />
                    </Col>
                    <Col lg="2">
                        <FormInput disabled value={lineItem.price} className="mb-2"
                            label={t('common.price')} type="number" min="0.01" step="0.01"
                            onChange={(e) => handleChange({ price: e.target.value })}
                            required
                        />
                    </Col>
                    <Col lg="2">
                        <FormInput value={lineItem.taxRate} className="mb-2" label={t('jobs.taxPercent')} type="number" step='0.01' onChange={(e) => handleChange({ taxRate: e.target.value })} />
                    </Col>
                    <Col lg="2">
                        <FormInput disabled value={lineItem.priceWithTax} className="mb-2" label={t('common.total')}
                            type="number" min="0.01" step="0.01" required
                            onChange={(e) => handleChange({ priceWithTax: e.target.value })}
                        />
                    </Col>
                </Row>
            </Form>
        </SbrDrawer>
    );
};

export default SetLineItemModal;
